import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { MarkdownView } from './MarkdownView';
import { TEMPLATES, LOGO_SIZES } from './welcomeTemplates';

/**
 * WelcomePageEditor — Admin editor for the per-org public "Welcome"
 * marketing page. Mounted inside Org Settings. Lets the org owner
 * edit the headline/tagline/about/services/hours/hero, toggle
 * `published` on/off, preview markdown live, copy the public URL,
 * download a QR code, and share to WhatsApp/Facebook/Twitter.
 */
export function WelcomePageEditor() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);   // { slug, welcome_page, public_url_path }
  const [form, setForm] = useState(null);
  const [previewFor, setPreviewFor] = useState(null); // 'about' | 'services' | 'hours' | null

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.getMyWelcomePage();
        setData(res);
        setForm({ ...res.welcome_page });
      } catch (err) {
        setError(err?.message || 'Failed to load welcome page');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const publicUrl = useMemo(() => {
    if (!data?.slug) return '';
    // Prefer the current browser origin so previewing on dev/preview builds
    // uses the correct host.
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/welcome/${data.slug}`;
  }, [data?.slug]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (extraPatch = {}) => {
    if (!form) return;
    setSaving(true);
    setError('');
    try {
      const patch = { ...form, ...extraPatch };
      const res = await api.updateMyWelcomePage(patch);
      setData(res);
      setForm({ ...res.welcome_page });
      toast.success('Welcome page saved');
    } catch (err) {
      const msg = err?.message || 'Save failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async () => {
    if (!form) return;
    await save({ published: !form.published });
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success('Public link copied');
    } catch (_e) {
      toast.error('Unable to copy — please copy manually');
    }
  };

  if (loading) {
    return <div className="card" data-testid="welcome-editor-loading">Loading welcome page…</div>;
  }
  if (!data || !form) {
    return (
      <div className="card" data-testid="welcome-editor-error">
        <p style={{ color: 'hsl(0 60% 35%)' }}>{error || 'Unable to load welcome page.'}</p>
      </div>
    );
  }

  const qrUrl = publicUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(publicUrl)}&size=220x220&margin=8`
    : '';

  return (
    <div className="card" data-testid="welcome-page-editor" style={{ padding: '1.25rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem',
      }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>🌐 Public Welcome Page</h2>
          <p style={{ margin: '0.15rem 0 0', fontSize: '0.82rem', color: 'hsl(var(--muted-foreground))' }}>
            A branded landing page you can share with prospective parents. They fill in the signup
            form and land straight in your <strong>Pending Parents</strong> queue.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <PublishBadge published={form.published} />
          <button type="button" className="btn btn-ghost btn-sm"
                  onClick={togglePublish} disabled={saving}
                  data-testid="welcome-editor-publish-toggle">
            {form.published ? 'Unpublish' : 'Publish'}
          </button>
        </div>
      </div>

      {form.published && (
        <div style={{
          background: 'hsl(140 55% 92%)', color: 'hsl(140 55% 22%)',
          border: '1px solid hsl(140 55% 78%)',
          padding: '0.6rem 0.85rem', borderRadius: 10, marginBottom: '1rem',
          fontSize: '0.85rem',
        }} data-testid="welcome-editor-live-banner">
          ✅ Live at{' '}
          <a href={publicUrl} target="_blank" rel="noreferrer"
             data-testid="welcome-editor-public-url"
             style={{ color: 'inherit', fontWeight: 700, textDecoration: 'underline' }}>
            {publicUrl}
          </a>
        </div>
      )}

      {error && (
        <div className="card" style={{
          borderLeft: '4px solid hsl(0 65% 50%)', color: 'hsl(0 60% 35%)',
          fontSize: '0.85rem', margin: '0 0 0.75rem', padding: '0.5rem 0.75rem',
        }}>{error}</div>
      )}

      {/* Share / QR row */}
      <div style={{
        display: 'grid', gap: '1rem',
        gridTemplateColumns: 'minmax(0, 1fr) 220px',
        marginBottom: '1.25rem',
      }} className="welcome-share-row">
        <div style={{
          border: '1px solid hsl(var(--border))', borderRadius: 10, padding: '0.75rem',
          background: 'hsl(var(--muted) / 0.35)',
        }}>
          <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: '0.35rem' }}>Share link</div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            border: '1px solid hsl(var(--border))', borderRadius: 8, background: 'hsl(var(--background))', padding: '0.4rem 0.55rem',
            marginBottom: '0.55rem',
          }}>
            <input readOnly value={publicUrl}
                   data-testid="welcome-editor-share-url"
                   onFocus={(e) => e.target.select()}
                   style={{
                     flex: 1, minWidth: 0, border: 'none', outline: 'none',
                     fontSize: '0.85rem', fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                     background: 'transparent', color: 'hsl(var(--foreground))',
                   }} />
            <button type="button" className="btn btn-primary btn-sm"
                    onClick={copyLink}
                    data-testid="welcome-editor-copy-link">Copy</button>
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            <ShareButton
              label="WhatsApp"
              icon="💬"
              testId="welcome-editor-share-whatsapp"
              url={`https://wa.me/?text=${encodeURIComponent(`Check out our nursery: ${publicUrl}`)}`}
            />
            <ShareButton
              label="Facebook"
              icon="📘"
              testId="welcome-editor-share-facebook"
              url={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(publicUrl)}`}
            />
            <ShareButton
              label="X"
              icon="🐦"
              testId="welcome-editor-share-x"
              url={`https://twitter.com/intent/tweet?text=${encodeURIComponent('Come join our nursery!')}&url=${encodeURIComponent(publicUrl)}`}
            />
            <ShareButton
              label="Email"
              icon="✉️"
              testId="welcome-editor-share-email"
              url={`mailto:?subject=${encodeURIComponent('Come and visit our nursery')}&body=${encodeURIComponent(`We'd love to show you around. Here's our welcome page: ${publicUrl}`)}`}
            />
          </div>
          <p style={{ margin: '0.6rem 0 0', fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }}>
            {form.published
              ? 'Share this link anywhere — in your email signature, on social, or as a QR code on flyers.'
              : 'Publish the page first — the link only works when live.'}
          </p>
        </div>

        <div style={{
          border: '1px solid hsl(var(--border))', borderRadius: 10, padding: '0.75rem',
          background: 'hsl(var(--muted) / 0.35)',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: '0.35rem' }}>QR code</div>
          {publicUrl ? (
            <>
              <img
                src={qrUrl}
                alt="QR code to public welcome page"
                data-testid="welcome-editor-qr-img"
                style={{ width: '100%', maxWidth: 180, aspectRatio: '1 / 1', background: '#fff', padding: 4, borderRadius: 6 }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <a href={qrUrl} download={`welcome-${data.slug}.png`}
                 className="btn btn-ghost btn-sm"
                 data-testid="welcome-editor-qr-download"
                 style={{ marginTop: '0.35rem', display: 'inline-block' }}>Download PNG</a>
            </>
          ) : <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.8rem' }}>—</div>}
        </div>
      </div>

      {/* Editor form */}
      <div style={{ display: 'grid', gap: '1rem' }}>
        {/* Template picker — 5 curated visual presets. Each writes to
            `welcome_page.template`; the public renderer reads that key
            and applies the matching design tokens. Owner keeps their
            brand primary_color across every template. */}
        <div data-testid="welcome-editor-template-picker">
          <div style={{
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            marginBottom: 8, gap: '0.5rem', flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>Template</div>
              <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                Pick a look — your brand colour, logo and content stay the same.
              </div>
            </div>
            <span style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }}>
              Current: <strong>{TEMPLATES.find((tm) => tm.id === (form.template || 'classic'))?.name}</strong>
            </span>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '0.6rem',
          }}>
            {TEMPLATES.map((tpl) => {
              const active = (form.template || 'classic') === tpl.id;
              return (
                <button
                  key={tpl.id}
                  type="button"
                  onClick={() => set('template')(tpl.id)}
                  data-testid={`welcome-editor-template-${tpl.id}`}
                  aria-pressed={active}
                  style={{
                    textAlign: 'left',
                    padding: '0.75rem',
                    border: active ? '2px solid hsl(var(--primary))' : '1px solid hsl(var(--border))',
                    borderRadius: 12,
                    background: active ? 'hsl(var(--primary) / 0.06)' : 'hsl(var(--background))',
                    cursor: 'pointer',
                    boxShadow: active ? '0 4px 14px hsl(var(--primary) / 0.18)' : 'none',
                    transition: 'transform 160ms ease, box-shadow 160ms ease',
                    position: 'relative',
                  }}
                >
                  {/* Swatch preview */}
                  <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                    {tpl.swatch.map((c, i) => (
                      <span key={i} style={{
                        display: 'inline-block',
                        width: 26, height: 26, borderRadius: 6,
                        background: c,
                        border: '1px solid rgba(0,0,0,0.08)',
                      }} />
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{tpl.name}</span>
                    {active && (
                      <span style={{
                        display: 'inline-block',
                        padding: '1px 6px', borderRadius: 999,
                        fontSize: '0.65rem', fontWeight: 700,
                        background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))',
                      }}>SELECTED</span>
                    )}
                  </div>
                  <div style={{
                    marginTop: 2, fontSize: '0.72rem',
                    color: 'hsl(var(--muted-foreground))', lineHeight: 1.4,
                  }}>{tpl.tagline}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Logo size — controls how big the org's logo appears in the
            top-bar and the hero band on the public page. */}
        <div data-testid="welcome-editor-logo-size">
          <div style={{
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            marginBottom: 8, gap: '0.5rem', flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>Logo size</div>
              <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                Controls the top-bar and hero logo dimensions. Uploads a small logo? Pick Small.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {Object.entries(LOGO_SIZES).map(([key, meta]) => {
              const active = (form.logo_size || 'm') === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => set('logo_size')(key)}
                  data-testid={`welcome-editor-logo-size-${key}`}
                  aria-pressed={active}
                  style={{
                    flex: '1 1 140px',
                    padding: '0.6rem 0.85rem',
                    border: active ? '2px solid hsl(var(--primary))' : '1px solid hsl(var(--border))',
                    borderRadius: 10,
                    background: active ? 'hsl(var(--primary) / 0.06)' : 'hsl(var(--background))',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>{meta.label}</div>
                  <div style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', marginTop: 2 }}>
                    Up to {meta.topBar}px · {meta.description}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <TextField label="Headline"
                   maxLength={120}
                   value={form.headline || ''}
                   onChange={set('headline')}
                   placeholder={`Welcome to ${data.slug}`}
                   testId="welcome-editor-headline" />
        <TextField label="Tagline"
                   maxLength={200}
                   value={form.tagline || ''}
                   onChange={set('tagline')}
                   placeholder="A short line under the headline (e.g. 'Ofsted-outstanding childcare in North London')"
                   testId="welcome-editor-tagline" />
        <TextField label="Hero image URL (optional)"
                   maxLength={500}
                   value={form.hero_image_url || ''}
                   onChange={set('hero_image_url')}
                   placeholder="https://…"
                   testId="welcome-editor-hero-url"
                   help="Paste a public image URL. Recommended 1600 × 900px." />
        <TextField label="Signup button label"
                   maxLength={60}
                   value={form.cta_text || ''}
                   onChange={set('cta_text')}
                   placeholder="Register your interest"
                   testId="welcome-editor-cta" />

        <MarkdownField label="About us"
                       value={form.about_md || ''}
                       onChange={set('about_md')}
                       previewOpen={previewFor === 'about'}
                       onTogglePreview={() => setPreviewFor(previewFor === 'about' ? null : 'about')}
                       testId="welcome-editor-about"
                       placeholder="Tell prospective parents what makes your nursery special. Markdown supported."
                       maxLength={8000} />

        <MarkdownField label="What we offer"
                       value={form.services_md || ''}
                       onChange={set('services_md')}
                       previewOpen={previewFor === 'services'}
                       onTogglePreview={() => setPreviewFor(previewFor === 'services' ? null : 'services')}
                       testId="welcome-editor-services"
                       placeholder={'- Full day-care\n- Breakfast & after-school club\n- Holiday club'}
                       maxLength={8000} />

        <div style={{
          display: 'grid', gap: '0.5rem',
          padding: '0.85rem', border: '1px solid hsl(var(--border))', borderRadius: 10,
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>
            <input type="checkbox"
                   checked={!!form.show_opening_hours}
                   onChange={(e) => set('show_opening_hours')(e.target.checked)}
                   data-testid="welcome-editor-toggle-hours" />
            Show opening hours section
          </label>
          {form.show_opening_hours && (
            <MarkdownField label=""
                           value={form.opening_hours_md || ''}
                           onChange={set('opening_hours_md')}
                           previewOpen={previewFor === 'hours'}
                           onTogglePreview={() => setPreviewFor(previewFor === 'hours' ? null : 'hours')}
                           testId="welcome-editor-hours"
                           placeholder={'Mon-Fri: 7:30am - 6:30pm\nSat: 9:00am - 1:00pm\nSun: closed'}
                           maxLength={2000} />
          )}
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>
          <input type="checkbox"
                 checked={!!form.show_contact}
                 onChange={(e) => set('show_contact')(e.target.checked)}
                 data-testid="welcome-editor-toggle-contact" />
          Show contact section (address, phone, email pulled from your org settings)
        </label>

        {/* Analytics & tracking — advanced/optional. Presets validated on
            the backend; custom snippet is an escape hatch for anything else
            (Hotjar, LinkedIn Insight, TikTok Pixel, etc.). */}
        <div
          data-testid="welcome-editor-analytics-section"
          style={{
            padding: '1rem',
            border: '1px solid hsl(var(--border))',
            borderRadius: 12,
            background: 'hsl(var(--muted) / 0.30)',
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.35rem',
          }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem' }}>📊 Analytics &amp; tracking</h3>
            <span style={{
              fontSize: '0.7rem', fontWeight: 700,
              padding: '2px 8px', borderRadius: 999,
              background: 'hsl(35 95% 92%)', color: 'hsl(35 85% 30%)',
            }}>OPTIONAL</span>
          </div>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.55 }}>
            Paste your provider IDs below and we'll load their standard tracking script when a
            visitor lands on your welcome page. A shared <code>signup_completed</code> conversion event fires on
            all providers whenever a parent submits the form.
          </p>
          <div style={{
            padding: '0.55rem 0.75rem', borderRadius: 8,
            background: 'hsl(140 55% 92%)',
            border: '1px solid hsl(140 55% 78%)',
            color: 'hsl(140 55% 22%)', fontSize: '0.78rem', lineHeight: 1.5,
            marginBottom: '0.9rem',
          }} data-testid="welcome-editor-analytics-gdpr">
            ✅ <strong>Consent-first tracking:</strong> Your welcome page now shows an automatic
            cookie consent banner to every new visitor. No analytics scripts load until they click
            &quot;Accept all&quot;. Visitors can change their mind at any time via the &quot;Cookie preferences&quot; link
            in your page footer. This satisfies EU/UK GDPR/PECR requirements out of the box.
          </div>

          <TextField label="Privacy notice URL (recommended)"
                     maxLength={500}
                     value={form.privacy_policy_url || ''}
                     onChange={set('privacy_policy_url')}
                     placeholder="https://your-nursery.co.uk/privacy"
                     testId="welcome-editor-privacy-url"
                     help="Shown as a link in the cookie banner AND in your page footer. Best practice for lawful analytics." />

          <TextField label="Google Analytics 4 measurement ID"
                     maxLength={40}
                     value={form.ga4_measurement_id || ''}
                     onChange={set('ga4_measurement_id')}
                     placeholder="G-XXXXXXXXXX"
                     testId="welcome-editor-ga4"
                     help="From Google Analytics → Admin → Data Streams. Also accepts AW-, GT-, DC-, MC- prefixes." />
          <TextField label="Facebook Pixel ID"
                     maxLength={40}
                     value={form.facebook_pixel_id || ''}
                     onChange={set('facebook_pixel_id')}
                     placeholder="1234567890123456"
                     testId="welcome-editor-fb-pixel"
                     help="Numeric only. From Meta Events Manager → Data Sources → Your Pixel." />
          <TextField label="Plausible domain"
                     maxLength={253}
                     value={form.plausible_domain || ''}
                     onChange={set('plausible_domain')}
                     placeholder="nursery.co.uk"
                     testId="welcome-editor-plausible"
                     help="Enter the bare hostname you registered with Plausible — no https:// or trailing slash." />

          <div style={{ marginTop: '0.75rem' }} data-testid="welcome-editor-custom-head">
            <div style={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
              marginBottom: 4,
            }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                Custom &lt;head&gt; snippet
                <span style={{
                  marginLeft: 6, fontSize: '0.65rem', fontWeight: 700,
                  padding: '1px 6px', borderRadius: 999,
                  background: 'hsl(0 65% 92%)', color: 'hsl(0 60% 35%)',
                }}>ADVANCED</span>
              </div>
              <span style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }}>
                {(form.custom_head_snippet || '').length}/4000
              </span>
            </div>
            <textarea
              rows={4}
              value={form.custom_head_snippet || ''}
              onChange={(e) => set('custom_head_snippet')(e.target.value)}
              placeholder={"<!-- Anything you want in <head>: Hotjar, LinkedIn Insight, TikTok Pixel, etc. -->\n<script>...</script>"}
              maxLength={4000}
              data-testid="welcome-editor-custom-head-input"
              spellCheck={false}
              style={{
                width: '100%', padding: '0.55rem 0.7rem',
                border: '1px solid hsl(var(--border))', borderRadius: 8,
                fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                fontSize: '0.8rem', background: 'hsl(var(--background))',
                resize: 'vertical',
              }}
            />
            <div style={{ marginTop: 6, fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.5 }}>
              Injected verbatim into the page's <code>&lt;head&gt;</code>. Only paste code from sources you trust —
              a malicious snippet could hijack visitors' sessions on your welcome page.
            </div>
          </div>
        </div>
      </div>

      <div style={{
        marginTop: '1.25rem',
        display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end',
      }}>
        <a href={publicUrl} target="_blank" rel="noreferrer"
           className="btn btn-ghost"
           data-testid="welcome-editor-open-live"
           style={{ pointerEvents: form.published ? 'auto' : 'none', opacity: form.published ? 1 : 0.5 }}>
          ↗ Open live page
        </a>
        <button type="button" className="btn btn-primary"
                onClick={() => save()} disabled={saving}
                data-testid="welcome-editor-save">
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <style>{`
        @media (max-width: 720px) {
          .welcome-share-row { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

function PublishBadge({ published }) {
  return (
    <span data-testid="welcome-editor-publish-badge" style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 999,
      fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.3,
      background: published ? 'hsl(140 55% 92%)' : 'hsl(var(--muted))',
      color: published ? 'hsl(140 55% 25%)' : 'hsl(var(--muted-foreground))',
    }}>
      {published ? '● LIVE' : '○ DRAFT'}
    </span>
  );
}

function TextField({ label, value, onChange, placeholder, maxLength, testId, help }) {
  return (
    <label style={{ display: 'block' }} data-testid={testId}>
      <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        data-testid={`${testId}-input`}
        style={{
          width: '100%', padding: '0.55rem 0.7rem',
          border: '1px solid hsl(var(--border))', borderRadius: 8,
          fontSize: '0.9rem', background: 'hsl(var(--background))',
        }}
      />
      {help && <div style={{ marginTop: 4, fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }}>{help}</div>}
    </label>
  );
}

function MarkdownField({ label, value, onChange, placeholder, maxLength, previewOpen, onTogglePreview, testId }) {
  return (
    <div data-testid={testId}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 4,
      }}>
        {label && <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{label}</div>}
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }}>
            {(value || '').length}/{maxLength}
          </span>
          <button type="button" className="btn btn-ghost btn-sm"
                  onClick={onTogglePreview}
                  data-testid={`${testId}-preview-toggle`}>
            {previewOpen ? 'Edit' : 'Preview'}
          </button>
        </div>
      </div>
      {previewOpen ? (
        <div style={{
          padding: '0.65rem 0.75rem', border: '1px solid hsl(var(--border))', borderRadius: 8,
          minHeight: 100, background: 'hsl(var(--muted) / 0.3)',
        }} data-testid={`${testId}-preview`}>
          <MarkdownView source={value} />
        </div>
      ) : (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={6}
          data-testid={`${testId}-input`}
          style={{
            width: '100%', padding: '0.55rem 0.7rem',
            border: '1px solid hsl(var(--border))', borderRadius: 8,
            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
            fontSize: '0.85rem', background: 'hsl(var(--background))',
            resize: 'vertical',
          }}
        />
      )}
    </div>
  );
}

function ShareButton({ label, icon, url, testId }) {
  return (
    <a href={url} target="_blank" rel="noreferrer"
       className="btn btn-ghost btn-sm"
       data-testid={testId}
       style={{ fontSize: '0.8rem' }}>
      <span aria-hidden="true">{icon}</span> {label}
    </a>
  );
}

export default WelcomePageEditor;
